import { ApiError, uuidv7 } from "@saas/api-client";
import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";

export interface AsyncState<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  reload: () => void;
}

/**
 * Minimal data loader: runs `load` on mount and when deps change; optional polling interval. The last result stays
 * visible while a reload runs; `loading` is true until the result for the current deps/version has arrived.
 * `deps` must be primitives (ids), as with React's own dependency arrays.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[], pollMs?: number): AsyncState<T> {
  const [version, setVersion] = useState(0);
  const [result, setResult] = useState<{ key?: string; data?: T; error?: Error }>({});
  const key = JSON.stringify([...deps, version]);
  // Always calls the latest `load` without making it an effect dependency (it is a new function every render).
  const run = useEffectEvent(() => load());

  useEffect(() => {
    let cancelled = false;
    run().then(
      (value) => {
        if (!cancelled) setResult({ key, data: value });
      },
      (err: unknown) => {
        if (!cancelled) setResult((previous) => ({ key, data: previous.data, error: err instanceof Error ? err : new Error(String(err)) }));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [key]);

  useEffect(() => {
    if (!pollMs) return;
    const timer = setInterval(() => setVersion((v) => v + 1), pollMs);
    return () => clearInterval(timer);
  }, [pollMs]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { data: result.data, error: result.error, loading: result.key !== key, reload };
}

/** Runs a command; exposes pending state, the last error (with field errors) and a success flag. */
export function useCommand() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | Error>();

  const run = useCallback(async (command: () => Promise<unknown>): Promise<boolean> => {
    setPending(true);
    setError(undefined);
    try {
      await command();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      return false;
    } finally {
      setPending(false);
    }
  }, []);

  const fieldError = (field: string): string | undefined =>
    error instanceof ApiError ? error.problem.errors?.[field]?.join(" ") : undefined;

  return { run, pending, error, fieldError, clearError: () => setError(undefined) };
}

/**
 * The caller-generated id (UUID v7) of a create command, kept across retries: after an ambiguous failure (network
 * error, timeout, 5xx) the command may already have run, and re-sending it with the same id is an idempotent no-op,
 * while a new id would create a duplicate. A new id is issued when the form values change or after `done()`.
 */
export function usePendingId() {
  // Every set of values sent since the last success keeps its id: values that were sent, edited and then restored
  // (e.g. after a lost response) are re-sent with their original id, not a new one (A17.L2).
  const pending = useRef(new Map<string, string>());

  /** The id to send for these form values (the same one again when a previous attempt with them failed). */
  const idFor = useCallback((...values: unknown[]): string => {
    const key = JSON.stringify(values);
    let id = pending.current.get(key);
    if (id === undefined) {
      id = uuidv7();
      pending.current.set(key, id);
    }
    return id;
  }, []);

  /** Call after the command succeeded: the next submit is a new entity. */
  const done = useCallback(() => {
    pending.current.clear();
  }, []);

  return { idFor, done };
}
